
import * as THREE from "../../libs/three.js/build/three.module.js";
import { Volume } from "./Volume.js";
import { Utils } from "../utils.js";
import { MAX_CLIP_PRISM_VERTICES } from "../defines.js";

/**
 * YDPolygonVolume — 异形多边形棱柱包围盒（俯视采点 + Z 高度）。
 *
 * 与 PolygonClipVolume（插入时刻相机的 NDC 楔形体，顶点存屏幕坐标、深度无界）
 * 语义完全不同：本类是「世界坐标系的多边形棱柱」——俯视图下采 N 个点决定 XY
 * 平面的多边形形状，zMin/zMax 决定上下底高度，构成一个竖直棱柱体。
 *
 * 数据约定：
 * - this.points    局部坐标（相对 volume 原点）的顶点数组，XY 是多边形轮廓，Z 不参与形状；
 * - this.zMin/zMax 局部 Z 范围（zMax = 平坦顶盖，zMin = 裁剪底）；
 * - setFromWorldPoints() 把世界采点换算成上述局部数据：原点取顶点 XY 质心 + 棱柱 Z 中点
 *   （局部 zMin/zMax = ∓h/2，几何关于原点对称），之后整体平移/旋转/缩放走 Object3D
 *   标准变换——缩放经 _onScaleChanged 换算回几何，scale 恒为 1。
 *
 * 子对象命名刻意用 this.box（半透明棱柱面）与 this.frame（顶/底多边形环 + 竖边
 * 的 LineSegments）——VolumeTool.render/renderDepth 按名取且有 null 守卫，零改动兼容；
 * dispose 后二者被置 null，update() 有守卫可安全跳过。
 *
 * 裁剪接入：viewer 每帧收集 clip===true 的本类实例，把局部顶点经 matrixWorld 变到
 * 世界坐标上传 shader（pointcloud.vs 的 pointInClipPrism，even-odd + Z 范围判定），
 * clipTask/clipMethod 三态语义与 clipboxes 完全一致；高斯侧由 SplatClipBridge 镜像。
 */
// 与 defines.js 的 MAX_CLIP_PRISM_VERTICES（shader/材质层共用）同源
export const MAX_POLYGON_VERTICES = MAX_CLIP_PRISM_VERTICES;

/**
 * 纯函数：点是否在多边形棱柱内（局部坐标系）。
 * XY 用 even-odd 射线交叉计数（向 -X 方向引射线），天然支持凹多边形与自交多边形
 * （语义与现有 NDC 多边形裁剪一致）；Z 用闭区间 [zMin, zMax]。
 *
 * @param {THREE.Vector3|{x,y,z}} p 待测点
 * @param {Array<{x,y,z}>} points 多边形顶点（XY 有效）
 * @param {number} zMin @param {number} zMax
 * @returns {boolean}
 */
export function pointInPolygonPrism(p, points, zMin, zMax) {
	if (!points || points.length < 3) {
		return false;
	}
	if (p.z < zMin || p.z > zMax) {
		return false;
	}

	let inside = false;
	const n = points.length;
	for (let i = 0, j = n - 1; i < n; j = i++) {
		const a = points[i];
		const b = points[j];

		// 边 a→b 跨越 p.y 所在水平线时求交点 x，交点在 p.x 左侧则计一次交叉
		if ((a.y > p.y) !== (b.y > p.y)) {
			const xCross = ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
			if (p.x < xCross) {
				inside = !inside;
			}
		}
	}

	return inside;
}

/**
 * 2D 线段相交测试（XY 平面，忽略 Z；与 even-odd 判定同一坐标语义）。
 * 规范交叉、共线重叠、端点/内点相触都算相交。相邻边共享端点属正常多边形
 * 拓扑，由调用方负责跳过，本函数不做豁免。
 */
export function segmentsIntersect2D(p1, p2, q1, q2) {
	const dir = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
	const onSegment = (a, b, p) =>
		Math.min(a.x, b.x) <= p.x && p.x <= Math.max(a.x, b.x) &&
		Math.min(a.y, b.y) <= p.y && p.y <= Math.max(a.y, b.y);

	const d1 = dir(q1, q2, p1);
	const d2 = dir(q1, q2, p2);
	const d3 = dir(p1, p2, q1);
	const d4 = dir(p1, p2, q2);

	if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
		((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
		return true;
	}
	if (d1 === 0 && onSegment(q1, q2, p1)) return true;
	if (d2 === 0 && onSegment(q1, q2, p2)) return true;
	if (d3 === 0 && onSegment(p1, p2, q1)) return true;
	if (d4 === 0 && onSegment(p1, p2, q2)) return true;
	return false;
}

/**
 * 采点校验：把 pt 接到开放链 chain 末尾是否合法——不与任何非相邻边交叉
 * （相邻边 = 与链尾共享端点的最后一条边），且 XY 不与已有顶点重合
 * （重合会造成零长边退化）。链长 <3 时不存在交叉可能，只查重合。
 * @returns {{ok: boolean, reason: 'cross'|'duplicate'|null}}
 */
export function validateChainAppend(pt, chain, epsSq = 1e-8) {
	for (const p of chain) {
		const dx = pt.x - p.x;
		const dy = pt.y - p.y;
		if (dx * dx + dy * dy < epsSq) {
			return { ok: false, reason: 'duplicate' };
		}
	}

	const n = chain.length;
	if (n < 3) {
		return { ok: true, reason: null };
	}
	const last = chain[n - 1];
	for (let i = 0; i < n - 2; i++) {
		if (segmentsIntersect2D(last, pt, chain[i], chain[i + 1])) {
			return { ok: false, reason: 'cross' };
		}
	}
	return { ok: true, reason: null };
}

/**
 * 闭合校验：闭合边 (chain[n-1]→chain[0]) 是否与链上其余边交叉
 * （跳过共享端点的首尾两条边）。链长 <3 恒合法（工具侧本就不允许闭合）。
 * @returns {{ok: boolean, reason: 'cross'|null}}
 */
export function validateChainClose(chain) {
	const n = chain.length;
	if (n < 3) {
		return { ok: true, reason: null };
	}
	for (let i = 1; i <= n - 3; i++) {
		if (segmentsIntersect2D(chain[n - 1], chain[0], chain[i], chain[i + 1])) {
			return { ok: false, reason: 'cross' };
		}
	}
	return { ok: true, reason: null };
}

/**
 * 拖拽校验：把开放链 chain 的第 idx 个顶点移到 pt 是否合法——受影响的
 * 相邻边（(idx-1,idx) 与 (idx,idx+1)，按链首尾不存在的那条略去）不与其余
 * 边交叉，且 pt 不与其他顶点重合。
 * @returns {{ok: boolean, reason: 'cross'|'duplicate'|null}}
 */
export function validateChainMove(pt, idx, chain, epsSq = 1e-8) {
	const n = chain.length;
	for (let i = 0; i < n; i++) {
		if (i === idx) {
			continue;
		}
		const dx = pt.x - chain[i].x;
		const dy = pt.y - chain[i].y;
		if (dx * dx + dy * dy < epsSq) {
			return { ok: false, reason: 'duplicate' };
		}
	}

	if (n < 3) {
		return { ok: true, reason: null };
	}

	const affected = []; // [anchor=该边在链上的固定端点索引, 端点a, 端点b]
	if (idx > 0) {
		affected.push([idx - 1, chain[idx - 1], pt]);
	}
	if (idx < n - 1) {
		affected.push([idx + 1, pt, chain[idx + 1]]);
	}

	// 豁免必须按「受影响边的链端点 anchor」逐对判断：与 anchor 相接的链边是
	// 正常相邻拓扑；但不能因为边 i 触及某条受影响边就连另一条受影响边的
	// 检测一起跳过（非相邻的新边可能恰好穿过它）
	for (let i = 0; i < n - 1; i++) {
		for (const [anchor, ea, eb] of affected) {
			if (i === anchor || i + 1 === anchor) {
				continue;
			}
			if (segmentsIntersect2D(ea, eb, chain[i], chain[i + 1])) {
				return { ok: false, reason: 'cross' };
			}
		}
	}
	return { ok: true, reason: null };
}

/**
 * 全对自交检查（开放链，不含闭合边）：任意两条非相邻边相交即自交。
 * 用于独立文件导入时的数据体检（warn 提示但不拒绝，even-odd 本身能处理）。
 */
export function chainSelfIntersects(chain) {
	const n = chain.length;
	for (let i = 0; i < n - 1; i++) {
		for (let j = i + 2; j < n - 1; j++) {
			if (segmentsIntersect2D(chain[i], chain[i + 1], chain[j], chain[j + 1])) {
				return true;
			}
		}
	}
	return false;
}

export class YDPolygonVolume extends Volume {

	constructor(args = {}) {
		super(args);

		this.constructor.counter = (this.constructor.counter === undefined) ? 0 : this.constructor.counter + 1;
		this.name = 'ydpolygon_' + this.constructor.counter;

		// 局部坐标顶点；空数组 = 尚未采点
		this.points = [];
		// 局部 Z 范围：zMax = 平坦顶盖高（创建时 = 最高环点），zMin = 裁剪底
		// （创建时 = 最低环点）；连线形态下底环网格按逐点 Z 帘式起伏
		this.zMin = 0;
		this.zMax = 0;
		// 形态开关：false = 连线（底环逐点跟随顶点 Z，帘式贴点云起伏）；
		// true = 柱状（所有点取最高/最低 Z，平底 zMin、平顶 zMax 的直柱，
		// 外观与裁剪范围完全一致）。面板勾选框切换，随工程/JSON 持久化
		this.columnMode = false;
		// 选中显示模式：true = 选中只显示顶点小圆点（不出坐标操作器，拖点编辑为主）；
		// false = 选中只显示坐标操作器（不出顶点球）。两个消费点：本类
		// _syncMarkerVisibility（顶点球）与 TransformationTool.update（gizmo 逐帧抑制）。
		// 运行时翻转走 setVertexMarkersOnSelect
		this.vertexMarkersOnSelect = true;
		// 不显示棱柱中央的总体积标签（用户约定：多边形棱柱不展示 m³ 数值；
		// Volume.js:291 与本类 update() 都以该开关控制 label.visible）
		this.showVolumeLabel = false;

		this.material = new THREE.MeshBasicMaterial({
			color: 0x00ffff,
			transparent: true,
			opacity: 0.3,
			depthTest: true,
			depthWrite: false,
			side: THREE.DoubleSide
		});
		this.box = new THREE.Mesh(new THREE.BufferGeometry(), this.material);
		this.add(this.box);

		this.frame = new THREE.LineSegments(
			new THREE.BufferGeometry(),
			new THREE.LineBasicMaterial({ color: 0x000000 })
		);
		this.add(this.frame);

		// 退化高度（< 1cm）只在进入退化态时 warn 一次，退出后重置
		this._degenerateWarned = false;

		// —— gizmo 缩放换算状态 ——
		// TransformationTool 拖缩放手柄是增量式 scale.add(diff) + position.add(diff/2)
		// （TransformationTool.js:775-793），其「对面锚定」数学只对单位几何（BoxVolume 的
		// ±0.5 box）精确；本类几何按真实尺寸烘焙，直接套用会整体上移/底面漂移。
		// 监听 scale_changed 把每轴增量换算回几何编辑（被拖面 1:1 跟随鼠标、对侧锚定），
		// 然后把 scale 归 1。_lastPos 是上一事件处理完的 position，用于求本事件增量；
		// 锚定按零旋转假设（采点工具保证 rotation=0）。
		this._lastPos = new THREE.Vector3();
		this.addEventListener('scale_changed', () => this._onScaleChanged());

		// —— 闭合后顶点编辑标记（children，选中时显示、可拖动改点）——
		// 采点会话的标记随会话移除；这里的标记是持久子对象，数据源就是 this.points，
		// 显隐由「是否被选中」门控（selection_changed），拖拽机制与 InputHandler 的
		// 通用 drag 监听体系一致（mousedown 命中带 drag 监听的对象自动发起）。
		this._markerGeometry = new THREE.SphereGeometry(0.4, 10, 10);
		this.vertexMarkers = [];
		this._markersActive = false;
		this._markerWired = false;
		this._markerDragRaf = 0;
		this._pendingMarkerDrag = null;

		this.update();
	}

	/**
	 * 用一串世界坐标采点重建整个棱柱（采点工具的实时预览与闭合都用它）。
	 * 原点 = 顶点 XY 质心 + Z 中点；局部顶点 = 世界点 - 原点，局部 zMin/zMax = ∓h/2。
	 * 超过 MAX_POLYGON_VERTICES 时告警并截断（保留前 MAX 个，即「自动闭合」语义）。
	 * @returns {YDPolygonVolume} this
	 */
	setFromWorldPoints(worldPoints) {
		let pts = worldPoints || [];
		if (pts.length > MAX_POLYGON_VERTICES) {
			console.warn(`[YDPolygonVolume] 顶点数超过上限 ${MAX_POLYGON_VERTICES}，已截断`);
			pts = pts.slice(0, MAX_POLYGON_VERTICES);
		}

		if (pts.length === 0) {
			this.points = [];
			this.position.set(0, 0, 0);
			this.update();
			return this;
		}

		let cx = 0, cy = 0;
		let zMin = Infinity, zMax = -Infinity;
		for (const p of pts) {
			cx += p.x;
			cy += p.y;
			zMin = Math.min(zMin, p.z);
			zMax = Math.max(zMax, p.z);
		}
		cx /= pts.length;
		cy /= pts.length;
		const zMid = (zMin + zMax) / 2;

		this.points = pts.map(p => new THREE.Vector3(p.x - cx, p.y - cy, p.z - zMid));
		this.zMin = zMin - zMid;
		this.zMax = zMax - zMid;
		this.position.set(cx, cy, zMid);
		this.rotation.set(0, 0, 0);
		this.update();
		return this;
	}

	/** 世界坐标顶点快照（当前 matrixWorld 下的实时值），供收集/导出/面板使用 */
	getWorldPoints(target = []) {
		target.length = 0;
		this.updateMatrixWorld();
		for (const p of this.points) {
			target.push(p.clone().applyMatrix4(this.matrixWorld));
		}
		return target;
	}

	update() {
		// dispose 后 box/frame 被置 null；clip setter 等仍可能触发 update，安全跳过
		if (!this.box || !this.frame) {
			return;
		}

		this._normalizeFrame();
		this._lastPos.copy(this.position);
		this._syncVertexMarkers();

		const n = this.points.length;
		const valid = n >= 3;

		// 与 BoxVolume 同约定：裁剪态隐藏体面与标签，线框保持可见
		this.box.visible = valid && !this._clip;
		this.frame.visible = valid;
		this.label.visible = valid && !this._clip && this.showVolumeLabel;

		const height = this.zMax - this.zMin;
		if (valid && height < 0.01) {
			if (!this._degenerateWarned) {
				console.warn(`[YDPolygonVolume] 棱柱高度 ${height.toFixed(4)} < 1cm（退化），裁剪可能看不到效果；可在属性面板调整 zMin/zMax`);
				this._degenerateWarned = true;
			}
		} else {
			this._degenerateWarned = false;
		}

		if (!valid) {
			this.box.geometry.dispose();
			this.box.geometry = new THREE.BufferGeometry();
			this.frame.geometry.dispose();
			this.frame.geometry = new THREE.BufferGeometry();
			return;
		}

		this._buildPrismGeometry();
	}

	/**
	 * 归一化成规范形：scale=1、原点=棱柱 Z 中点（局部 zMin/zMax = ∓h/2）。
	 * - 遗留 scale（旧工程 gizmo 缩放后保存的数据）烘焙进顶点/zMin/zMax，世界范围不变；
	 * - 旧工程「origin z=0、局部 z 存世界 Z」的数据在此迁移为居中形，世界位置不变；
	 * - 新数据（setFromWorldPoints / _onScaleChanged 产物）进来都是空操作。
	 * 原点居中后，任何绕原点的变换都关于棱柱自身对称，不会出现「整体上移」类漂移。
	 */
	_normalizeFrame() {
		const eps = 1e-9;
		const s = this.scale;
		if (Math.abs(s.x - 1) > eps || Math.abs(s.y - 1) > eps || Math.abs(s.z - 1) > eps) {
			for (const p of this.points) {
				p.x *= s.x;
				p.y *= s.y;
				p.z *= s.z;
			}
			this.zMin *= s.z;
			this.zMax *= s.z;
			s.set(1, 1, 1);
		}

		const zMid = (this.zMin + this.zMax) / 2;
		if (Math.abs(zMid) > eps) {
			for (const p of this.points) {
				p.z -= zMid;
			}
			this.zMin -= zMid;
			this.zMax -= zMid;
			this.position.z += zMid;
		}
	}

	/**
	 * gizmo 缩放手柄的几何换算（监听 scale_changed；TransformationTool 每次鼠标移动
	 * 派发一次，且先 position_changed 后 scale_changed，position 已含本事件增量）。
	 *
	 * gizmo 的增量语义：scale[a] += Δs、position[a] += Δp = diff[a]/2（diff 为鼠标沿轴
	 * 位移，pivot 每事件后更新到当前点）。我们上一事件已把 scale 归 1，所以本事件
	 * Δs = scale[a] − 1、Δp = position[a] − _lastPos[a]。
	 *
	 * 被拖的是哪个端面：(Δs>0)===(Δp>0) → 正端面（正手柄 Δs=2Δp 同号，负手柄反号）。
	 * 换算目标与 BoxVolume 手感一致：被拖面 1:1 跟随鼠标（Δ面 = 2Δp），对侧面锚定不动。
	 * Z：顶盖平坦（zMax）、底环帘式（逐点 Z）——顶手柄只重写 zMax、底手柄只重写 zMin，
	 * position 与顶点局部 Z 恢复原值（环标记不随调高漂移）；XY 把顶点按固定面缩放并
	 * 抵消 gizmo 的 position 增量。换算完 scale 归 1（不可换算的增量也归 1，保持增量语义）。
	 */
	_onScaleChanged() {
		if (!this.box || this.points.length < 3) {
			return;
		}

		const eps = 1e-9;
		let dirty = false;

		// —— Z：高度面跟随 ——
		const dz = this.scale.z - 1;
		if (Math.abs(dz) > eps) {
			const dpz = this.position.z - this._lastPos.z;
			this.scale.z = 1;
			if (Math.abs(dpz) > 1e-12) {
				const topFace = (dz > 0) === (dpz > 0);
				const prevPosZ = this.position.z - dpz;
				// 顶盖平坦（zMax）、底环帘式（逐点 Z，吸附点云不动）。Z 向缩放：
				// 顶手柄抬/降顶盖 zMax，底手柄抬/降裁剪底 zMin；position 与顶点
				// 局部 Z 都不动——环标记不随调高漂移，被拖面 1:1 跟随鼠标（Δ=2Δp）
				this.position.z = prevPosZ;
				if (topFace) {
					this.zMax = Math.max(this.zMax + 2 * dpz, this.zMin + 0.01);
				} else {
					this.zMin = Math.min(this.zMin + 2 * dpz, this.zMax - 0.01);
				}
				dirty = true;
			}
		}

		// —— X/Y：多边形顶点按固定面缩放 ——
		for (const axis of ['x', 'y']) {
			const ds = this.scale[axis] - 1;
			if (Math.abs(ds) <= eps) {
				continue;
			}
			const dp = this.position[axis] - this._lastPos[axis];
			this.scale[axis] = 1;
			if (Math.abs(dp) <= 1e-12) {
				continue;
			}
			const positiveFace = (ds > 0) === (dp > 0);
			let min = Infinity;
			let max = -Infinity;
			for (const p of this.points) {
				min = Math.min(min, p[axis]);
				max = Math.max(max, p[axis]);
			}
			const extent = Math.max(max - min, 1e-6);
			const newExtent = Math.max(extent + (positiveFace ? 2 * dp : -2 * dp), 1e-3);
			const f = newExtent / extent;
			const anchor = positiveFace ? min : max; // 固定面（局部坐标）
			for (const p of this.points) {
				p[axis] = anchor + (p[axis] - anchor) * f;
			}
			this.position[axis] -= dp; // 固定面世界位置不变：抵消 gizmo 的 position 增量
			dirty = true;
		}

		if (dirty) {
			this.update();
			// gizmo 派发的 position_changed 在我们修正 position 之前，补一次通知让
			// 面板/联动方拿到锚定后的值（无 scale_changed 派发，不会递归）
			this.dispatchEvent({ type: 'position_changed', object: this });
		}
		this._lastPos.copy(this.position);
	}

	// —— 闭合后顶点编辑标记 ————————————————————————————————

	/** viewer 可用后接线（VolumeTool.onAdd 注入 this.viewer；懒执行一次） */
	_wireMarkers() {
		if (this._markerWired || !this.viewer || !this.viewer.inputHandler) {
			return;
		}
		this._markerWired = true;

		// 选中门控：selection_changed 事件带 {oldSelection, selection}
		this._onSelectionChanged = (e) => {
			this._markersActive = (e.selection || []).includes(this);
			this._syncMarkerVisibility();
		};
		this.viewer.inputHandler.addEventListener('selection_changed', this._onSelectionChanged);

		// 逐帧恒定屏幕大小（15/projectedRadius，Measure/MeasuringTool 同款）；
		// VolumeTool 私有场景自带灯光，标记无需头灯
		this._onViewerUpdate = () => {
			if (!this._markersActive || this.vertexMarkers.length === 0) {
				return;
			}
			const camera = this.viewer.scene.getActiveCamera();
			const domElement = this.viewer.renderer.domElement;
			for (const marker of this.vertexMarkers) {
				const distance = camera.position.distanceTo(marker.getWorldPosition(new THREE.Vector3()));
				const pr = Utils.projectedRadius(1, camera, distance, domElement.clientWidth, domElement.clientHeight);
				marker.scale.setScalar(15 / pr);
			}
		};
		this.viewer.addEventListener('update', this._onViewerUpdate);
	}

	/** 标记数量/位置与 points 同步（子对象本地坐标 = 局部点，随 volume 变换整体移动） */
	_syncVertexMarkers() {
		if (this._disposed) {
			return;
		}
		this._wireMarkers();

		const n = this.points.length;
		while (this.vertexMarkers.length > n) {
			const marker = this.vertexMarkers.pop();
			this.remove(marker);
			marker.material.dispose();
		}
		while (this.vertexMarkers.length < n) {
			// Lambert 材质：由 volumeTool 私有场景灯光（半球光+平行光+环境光）照亮，
			// 与采点会话标记球（PolygonVolumeTool，同场景同材质）外观一致
			const marker = new THREE.Mesh(this._markerGeometry, new THREE.MeshLambertMaterial({ color: 0x00ffff }));
			marker.material.depthTest = false;
			marker.material.depthWrite = false;
			marker.name = 'vertex_marker';
			marker.renderOrder = 10;
			marker.addEventListener('drag', (e) => this._onVertexMarkerDrag(e));
			marker.addEventListener('drop', () => this._onVertexMarkerDrop());
			marker.addEventListener('mouseover', (e) => { e.object.material.emissive.setHex(0x888888); });
			marker.addEventListener('mouseleave', (e) => { e.object.material.emissive.setHex(0x000000); });
			this.add(marker);
			this.vertexMarkers.push(marker);
		}
		for (let i = 0; i < n; i++) {
			this.vertexMarkers[i].position.copy(this.points[i]);
		}
		this._syncMarkerVisibility();
	}

	_syncMarkerVisibility() {
		// 顶点球显隐 = 选中态 × 显示模式（vertexMarkersOnSelect=false 时选中只出 gizmo）
		const show = this._markersActive && this.vertexMarkersOnSelect !== false;
		for (const marker of this.vertexMarkers) {
			marker.visible = show;
		}
	}

	// 运行时切换选中显示模式：顶点球立即重算；gizmo 由 TransformationTool.update
	// 逐帧门控自愈，无需额外处理。返回当前值。
	setVertexMarkersOnSelect(v) {
		this.vertexMarkersOnSelect = !!v;
		this._syncMarkerVisibility();
		return this.vertexMarkersOnSelect;
	}

	_onVertexMarkerDrag(e) {
		// rAF 节流：GS WASM 射线检测单次数 ms，快速拖拽合并同帧多次 drag
		this._pendingMarkerDrag = e;
		if (this._markerDragRaf) {
			return;
		}
		this._markerDragRaf = requestAnimationFrame(() => {
			this._markerDragRaf = 0;
			const evt = this._pendingMarkerDrag;
			this._pendingMarkerDrag = null;
			if (evt) {
				this._processVertexMarkerDrag(evt);
			}
		});
	}

	_processVertexMarkerDrag(e) {
		if (this._disposed || !this.box || this.points.length < 3) {
			return;
		}
		const viewer = e.viewer || this.viewer;
		const camera = viewer.scene.getActiveCamera();
		const hit = Utils.getMouseIntersection(e.drag.end, camera, viewer, { noGsDragSlide: true });
		let world = null;
		if (hit) {
			world = hit.location;
		}
		// miss 即原位不动：顶点必须吸附点云/高斯表面，不允许落虚空
		// （noGsDragSlide 同时关掉 GS 路径的拖动期平面滑动假 hit）
		if (!world) {
			return;
		}

		const idx = this.vertexMarkers.indexOf(e.drag.object);
		if (idx === -1) {
			return;
		}

		// 世界 → 局部（走矩阵逆变换，兼容任意平移/旋转/缩放）；顶点 3D 跟随命中点，
		// 与采点会话拖动一致——吸附点云/高斯表面（含 Z 起伏）。Z 不参与 XY 多边形
		// 裁剪（着色器只取局部 XY + 平坦 zMin/zMax），线框底环与标记按逐点 Z 渲染
		const local = this.worldToLocal(world.clone());

		// 自交/重合防护：非法位置忽略本次事件，标记停在上一合法位置
		const verdict = validateChainMove(local, idx, this.points);
		if (!verdict.ok) {
			return;
		}
		this.points[idx].copy(local);
		// 实时重算高度范围 = 当前所有环点的最低/最高 Z（柱状「所有点取最高最低Z」
		// 的字面语义；连线模式下顶盖/裁剪底同样始终罩住整圈环点），点拖回低处盒顶
		// 实时缩回。重算只发生在拖点事件里——gizmo/面板调的高度保留到下一次拖点
		let lo = Infinity;
		let hi = -Infinity;
		for (const p of this.points) {
			lo = Math.min(lo, p.z);
			hi = Math.max(hi, p.z);
		}
		if (hi - lo < 0.01) {
			hi = lo + 0.01; // 退化下限 1cm（全环同高时盒子不消失），与 gizmo 换算一致
		}
		this.zMin = lo;
		this.zMax = hi;
		this.update(); // 重建棱柱 + 同步标记位置
	}

	_onVertexMarkerDrop() {
		if (this._markerDragRaf) {
			cancelAnimationFrame(this._markerDragRaf);
			this._markerDragRaf = 0;
		}
		this._pendingMarkerDrag = null;
	}

	/**
	 * 销毁清理（Scene.removeVolume 在 volume_removed 事件后调用，Scene.js:348）。
	 * 幂等：_disposed 守卫；标记材质逐球独立须逐个释放，几何共享一份。
	 */
	dispose() {
		if (this._disposed) {
			return;
		}
		this._disposed = true;

		if (this._markerWired && this.viewer) {
			this.viewer.inputHandler.removeEventListener('selection_changed', this._onSelectionChanged);
			this.viewer.removeEventListener('update', this._onViewerUpdate);
		}
		if (this._markerDragRaf) {
			cancelAnimationFrame(this._markerDragRaf);
			this._markerDragRaf = 0;
		}
		for (const marker of this.vertexMarkers) {
			marker.material.dispose();
		}
		this.vertexMarkers.length = 0;
		this._markerGeometry.dispose();

		if (this.box) {
			this.box.geometry.dispose();
			this.material.dispose();
			this.remove(this.box);
			this.box = null;
		}
		if (this.frame) {
			this.frame.geometry.dispose();
			this.frame.material.dispose();
			this.remove(this.frame);
			this.frame = null;
		}
		if (this.label) {
			this.remove(this.label);
			this.label = null;
		}
	}

	/**
	 * 由 this.points + zMin/zMax 重建 box（indexed 棱柱面）与 frame（线段对）。
	 * 顶/底盖用 ShapeUtils.triangulateShape（earcut，even-odd 语义）三角化，
	 * 凹多边形甚至自交多边形都能得到与裁剪判定一致的填充。
	 */
	_buildPrismGeometry() {
		const n = this.points.length;
		// 底环：columnMode = 柱状（平底 zMin，所有点取最低 Z）；否则连线（帘式，
		// 逐点跟随顶点 Z 吸附点云的 3D 起伏）。顶环恒为平坦顶盖 zMax（创建时 =
		// 最高环点，gizmo/面板可再抬）——高度不叠加顶点起伏。zMin/zMax 同时是
		// 裁剪范围（着色器 pointInClipPrism：局部 XY + 平坦 z 范围），柱状下外观
		// 与裁剪范围完全一致
		const zMax = this.zMax;
		const column = this.columnMode;

		// —— 棱柱面：底部 n 点 + 顶部 n 点，共 2n 个顶点 ——
		const positions = new Float32Array(n * 2 * 3);
		for (let i = 0; i < n; i++) {
			const p = this.points[i];
			positions[i * 3 + 0] = p.x;
			positions[i * 3 + 1] = p.y;
			positions[i * 3 + 2] = column ? this.zMin : p.z;
			positions[(n + i) * 3 + 0] = p.x;
			positions[(n + i) * 3 + 1] = p.y;
			positions[(n + i) * 3 + 2] = zMax;
		}

		const contour = this.points.map(p => new THREE.Vector2(p.x, p.y));
		const tris = THREE.ShapeUtils.triangulateShape(contour, []);

		const indices = [];
		for (const t of tris) {
			// 底盖法线朝 -Z，绕序反转；顶盖保持
			indices.push(t[0], t[2], t[1]);
			indices.push(n + t[0], n + t[1], n + t[2]);
		}
		for (let i = 0; i < n; i++) {
			const j = (i + 1) % n;
			// 侧面四边形拆两个三角形：bi→bj→tj、bi→tj→ti
			indices.push(i, j, n + j);
			indices.push(i, n + j, n + i);
		}

		const prismGeometry = new THREE.BufferGeometry();
		prismGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
		prismGeometry.setIndex(indices);
		prismGeometry.computeBoundingBox();
		prismGeometry.computeBoundingSphere();

		this.box.geometry.dispose();
		this.box.geometry = prismGeometry;
		this.boundingBox = prismGeometry.boundingBox;
		this.boundingSphere = prismGeometry.boundingSphere;

		// —— 线框：底环 n 段 + 顶环 n 段 + 竖边 n 段，共 6n 个端点 ——
		const framePositions = new Float32Array(n * 6 * 3);
		let cursor = 0;
		const pushSeg = (a, b) => {
			framePositions[cursor++] = a.x; framePositions[cursor++] = a.y; framePositions[cursor++] = a.z;
			framePositions[cursor++] = b.x; framePositions[cursor++] = b.y; framePositions[cursor++] = b.z;
		};
		for (let i = 0; i < n; i++) {
			const j = (i + 1) % n;
			const pi = this.points[i];
			const pj = this.points[j];
			const bi = column ? new THREE.Vector3(pi.x, pi.y, this.zMin) : pi;
			const bj = column ? new THREE.Vector3(pj.x, pj.y, this.zMin) : pj;
			pushSeg(bi, bj);
			pushSeg(new THREE.Vector3(pi.x, pi.y, zMax), new THREE.Vector3(pj.x, pj.y, zMax));
			pushSeg(bi, new THREE.Vector3(pi.x, pi.y, zMax));
		}

		const frameGeometry = new THREE.BufferGeometry();
		frameGeometry.setAttribute('position', new THREE.BufferAttribute(framePositions, 3));
		frameGeometry.computeBoundingBox();
		frameGeometry.computeBoundingSphere();

		this.frame.geometry.dispose();
		this.frame.geometry = frameGeometry;
	}

	raycast(raycaster, intersects) {
		if (!this.box) {
			return;
		}
		let is = [];
		this.box.raycast(raycaster, is);

		if (is.length > 0) {
			let I = is[0];
			intersects.push({
				distance: I.distance,
				object: this,
				point: I.point.clone()
			});
		}
	}

	/** 多边形面积（shoelace）× 棱柱高度 */
	getVolume() {
		const n = this.points.length;
		if (n < 3) {
			return 0;
		}
		let area2 = 0;
		for (let i = 0, j = n - 1; i < n; j = i++) {
			area2 += this.points[j].x * this.points[i].y - this.points[i].x * this.points[j].y;
		}
		return Math.abs(area2 / 2) * Math.abs(this.zMax - this.zMin);
	}

};
